import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  apiV2, ApiV2Error,
  type V2OnCallSchedule, type V2OnCallParticipant, type V2AlertChannel,
} from '@/lib/api-v2-client'
import { toast } from 'sonner'
import { Plus, Trash2, Clock } from 'lucide-react'
import { ProOnlyPage } from '@/components/pro-only-page'

export const Route = createFileRoute('/_authenticated/dashboard/alerts/on-call')({
  component: () => (
    <ProOnlyPage
      title="On-call schedules are on Pro"
      text="Rotate who is paged by day or week, with SMS and phone-call alerts. On-call schedules need a paid plan."
    >
      <OnCallPage />
    </ProOnlyPage>
  ),
})

const ROTATIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'daily', label: 'Daily' },
  { value: 'custom', label: 'Custom' },
] as const

function OnCallPage() {
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<V2OnCallSchedule | null>(null)

  const { data: schedulesRes, isLoading } = useQuery({
    queryKey: ['on-call-schedules'],
    queryFn: () => apiV2.listOnCallSchedules(),
  })
  const { data: channelsRes } = useQuery({
    queryKey: ['alert-channels'],
    queryFn: () => apiV2.listAlertChannels(),
  })
  const schedules = schedulesRes?.data ?? []
  const channels = channelsRes?.data ?? []

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiV2.deleteOnCallSchedule(id),
    onSuccess: () => {
      toast.success('Schedule deleted')
      setDeleteTarget(null)
      queryClient.invalidateQueries({ queryKey: ['on-call-schedules'] })
    },
    onError: () => toast.error('Failed to delete schedule'),
  })

  return (
    <div className="space-y-6 pb-20">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold">On-Call Schedules</h1>
          <p className="text-muted-foreground">
            Rotations that page whoever&rsquo;s on duty — wire one into an escalation step.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> New schedule
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />)}</div>
      ) : schedules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-lg bg-muted">
              <Clock className="h-7 w-7 text-primary" />
            </div>
            <p className="mb-1 text-lg font-medium">No on-call schedules yet</p>
            <p className="mx-auto mb-4 max-w-md text-sm text-muted-foreground">
              Define a rotation, then add it as a step in an escalation policy so incidents page the person on call.
            </p>
            <Button onClick={() => setCreateOpen(true)}><Plus className="mr-1 h-4 w-4" /> New schedule</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {schedules.map((s) => (
            <Card key={s.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">{s.name}</CardTitle>
                    <CardDescription>
                      {s.rotation_type} rotation · {s.timezone} · {s.participants.length} participant{s.participants.length !== 1 ? 's' : ''}
                    </CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" aria-label={`Delete schedule ${s.name}`} onClick={() => setDeleteTarget(s)}>
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <CurrentOnCall scheduleId={s.id} participants={s.participants} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateScheduleDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        channels={channels}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ['on-call-schedules'] })}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete schedule?</DialogTitle>
            <DialogDescription>
              This deletes <strong>{deleteTarget?.name}</strong> and its overrides. Escalation steps pointing at it will fall back to org channels.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" disabled={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}>
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** Live "who's on call now" for a schedule, with the participant's label. */
function CurrentOnCall({ scheduleId, participants }: { scheduleId: string; participants: V2OnCallParticipant[] }) {
  const { data } = useQuery({
    queryKey: ['on-call-current', scheduleId],
    queryFn: () => apiV2.getOnCallCurrent(scheduleId),
    refetchInterval: 60_000,
  })
  if (!data) return <p className="text-sm text-muted-foreground">Resolving…</p>
  if (!data.participant) {
    return <p className="text-sm text-muted-foreground">No one on call (empty rotation).</p>
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground">On call now:</span>
      <Badge variant="secondary">{data.participant.id}</Badge>
      {data.at && (
        <span className="text-muted-foreground">until {new Date(data.at).toLocaleString()}</span>
      )}
      <span className="text-xs text-muted-foreground">· rotation of {participants.map((p) => p.id).join(' → ')}</span>
    </div>
  )
}

function CreateScheduleDialog({
  open, onOpenChange, channels, onCreated,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  channels: V2AlertChannel[]
  onCreated: () => void
}) {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  const [name, setName] = useState('')
  const [timezone, setTimezone] = useState(browserTz)
  const [rotationType, setRotationType] = useState<'daily' | 'weekly' | 'custom'>('weekly')
  const [customHours, setCustomHours] = useState('168')
  const [anchorLocal, setAnchorLocal] = useState(() => new Date().toISOString().slice(0, 16))
  const [participants, setParticipants] = useState<V2OnCallParticipant[]>([{ id: '', channel_ids: [] }])

  const reset = () => {
    setName(''); setTimezone(browserTz); setRotationType('weekly'); setCustomHours('168')
    setAnchorLocal(new Date().toISOString().slice(0, 16)); setParticipants([{ id: '', channel_ids: [] }])
  }

  const mutation = useMutation({
    mutationFn: () =>
      apiV2.createOnCallSchedule({
        name: name.trim(),
        timezone: timezone.trim(),
        rotation_type: rotationType,
        rotation_length_secs: rotationType === 'custom' ? Math.max(1, Number(customHours)) * 3600 : undefined,
        anchor_at: new Date(anchorLocal).toISOString(),
        participants: participants.filter((p) => p.id.trim()).map((p) => ({ id: p.id.trim(), channel_ids: p.channel_ids })),
      }),
    onSuccess: () => {
      toast.success('Schedule created')
      onOpenChange(false); reset(); onCreated()
    },
    onError: (e) => {
      toast.error(e instanceof ApiV2Error ? e.message : 'Failed to create schedule')
    },
  })

  const toggleChannel = (idx: number, channelId: string) => {
    setParticipants((ps) => ps.map((p, i) => {
      if (i !== idx) return p
      const has = p.channel_ids.includes(channelId)
      return { ...p, channel_ids: has ? p.channel_ids.filter((c) => c !== channelId) : [...p.channel_ids, channelId] }
    }))
  }

  const canSubmit = name.trim() && timezone.trim() && participants.some((p) => p.id.trim())

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New on-call schedule</DialogTitle>
          <DialogDescription>Participants rotate in order; whoever&rsquo;s on call gets paged.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="oc-name">Name</Label>
            <Input id="oc-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Primary on-call" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="oc-tz">Timezone</Label>
              <Input id="oc-tz" value={timezone} onChange={(e) => setTimezone(e.target.value)} placeholder="Asia/Bangkok" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="oc-rotation">Rotation</Label>
              <Select value={rotationType} onValueChange={(v) => setRotationType(v as 'daily' | 'weekly' | 'custom')}>
                <SelectTrigger id="oc-rotation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROTATIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {rotationType === 'custom' && (
              <div className="space-y-1.5">
                <Label htmlFor="oc-hrs">Shift length (hours)</Label>
                <Input id="oc-hrs" type="number" min="1" value={customHours} onChange={(e) => setCustomHours(e.target.value)} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="oc-anchor">First handoff</Label>
              <Input id="oc-anchor" type="datetime-local" value={anchorLocal} onChange={(e) => setAnchorLocal(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Participants (rotate in order)</Label>
            {participants.map((p, idx) => (
              <div key={idx} className="rounded-md border p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    aria-label={`Person ${idx + 1}`}
                    value={p.id}
                    onChange={(e) => setParticipants((ps) => ps.map((x, i) => i === idx ? { ...x, id: e.target.value } : x))}
                    placeholder={`Person ${idx + 1} (name or id)`}
                  />
                  {participants.length > 1 && (
                    <Button variant="ghost" size="sm" aria-label={`Remove person ${idx + 1}`} onClick={() => setParticipants((ps) => ps.filter((_, i) => i !== idx))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {channels.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {channels.map((ch) => (
                      <label key={ch.id} className="flex items-center gap-1.5 rounded border px-2 py-1 text-xs cursor-pointer">
                        <input type="checkbox" checked={p.channel_ids.includes(ch.id)} onChange={() => toggleChannel(idx, ch.id)} />
                        {ch.name} <span className="text-muted-foreground">({ch.type})</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No alert channels yet — add some under Alerts first.</p>
                )}
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setParticipants((ps) => [...ps, { id: '', channel_ids: [] }])}>
              <Plus className="mr-1 h-4 w-4" /> Add participant
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!canSubmit || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Creating…' : 'Create schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
